/**
 * Single, guarded service-worker registration point for Xamseen.
 *
 * The offline shell must never register inside the Lovable editor preview, an
 * iframe, or dev — a stale cached shell there would serve deleted chunks and
 * white-screen the editor. In every refused context we also actively unregister
 * any previously installed `/sw.js` so returning browsers recover.
 */

const SW_URL = "/sw.js";

function isRefusedContext(): boolean {
  if (typeof window === "undefined") return true;
  if (!import.meta.env.PROD) return true;
  if (window.top !== window.self) return true;

  const host = window.location.hostname;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com")) return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;
  if (new URLSearchParams(window.location.search).has("sw") &&
      new URLSearchParams(window.location.search).get("sw") === "off") return true;

  return false;
}

async function unregisterAppServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(
      registrations
        .filter((r) => (r.active?.scriptURL ?? r.installing?.scriptURL ?? "").endsWith(SW_URL))
        .map((r) => r.unregister()),
    );
  } catch {
    // A failed cleanup must never break the app shell.
  }
}

export function registerServiceWorker() {
  if (isRefusedContext()) {
    void unregisterAppServiceWorker();
    return;
  }
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  navigator.serviceWorker.register(SW_URL, { scope: "/" }).catch(() => {
    // Offline shell is an enhancement; ignore registration failures.
  });
}

/** True when the browser currently reports no network connection. */
export function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}
